import { createStore } from 'vuex'
import { getToken, setToken, removeToken, getUserInfo, setUserInfo, removeUserInfo, clearAuth, getMallToken, setMallToken, removeMallToken, setUserId, setRememberPhone, getPendingShareId, removePendingShareId } from '@/utils/auth'
import { login, smsLogin as apiSmsLogin, sendSmsCode as apiSendSmsCode } from '@/api/auth'
import { getUserById, getMallUserInfo, getMallProducts, getMallCategories, getMallOrders } from '@/api/user'
import { getCardList, getCardById } from '@/api/card'
import { getFriendList as apiGetFriendList, deleteFriend } from '@/api/friend'
import ShareManager from '@/utils/share'

const userModule = {
  namespaced: true,
  state: {
    token: '',
    userInfo: {},
    isLogin: false,
    mallToken: '',
    mallInfo: { level: 0, integral: 0, balance: 0, invite: null }
  },
  getters: {
    hasToken: state => !!state.token,
    userInfo: state => state.userInfo,
    isLogin: state => state.isLogin,
    hasMallToken: state => !!state.mallToken,
    mallToken: state => state.mallToken,
    mallInfo: state => state.mallInfo
  },
  mutations: {
    SET_TOKEN(state, { token, rememberMe }) {
      state.token = token
      state.isLogin = !!token
      setToken(token, rememberMe)
    },
    SET_USER_INFO(state, userInfo) {
      if (!userInfo) return
      state.userInfo = { ...userInfo }
      setUserInfo(userInfo)
    },
    SET_MALL_TOKEN(state, mallToken) {
      state.mallToken = mallToken
      setMallToken(mallToken)
      // ====== 统一认证桥接：同时写入 CRMEB H5 能读的 localStorage ======
      // CRMEB H5 的 checkLogin() 会读 CRMEB_MALL_TOKEN 并桥接登录
      if (mallToken && typeof window !== 'undefined') {
        try {
          const expiresAt = Math.floor(Date.now() / 1000) + 86400 * 7 // 7 天
          localStorage.setItem('CRMEB_MALL_TOKEN', mallToken)
          localStorage.setItem('CRMEB_MALL_TOKEN_EXPIRES', String(expiresAt))
          console.log('🔗 已注入 CRMEB H5 mallToken 到 localStorage')
        } catch (e) {
          console.warn('写入 CRMEB mallToken 到 localStorage 失败:', e)
        }
      }
    },
    SET_MALL_INFO(state, mallInfo) {
      state.mallInfo = { ...state.mallInfo, ...mallInfo }
    },
    LOGOUT(state) {
      state.token = ''
      state.userInfo = {}
      state.isLogin = false
      state.mallToken = ''
      state.mallInfo = { level: 0, integral: 0, balance: 0, invite: null }
      clearAuth()
      removeMallToken()
      // ====== 清理 CRMEB H5 的桥接 token ======
      if (typeof window !== 'undefined') {
        try {
          localStorage.removeItem('CRMEB_MALL_TOKEN')
          localStorage.removeItem('CRMEB_MALL_TOKEN_EXPIRES')
        } catch (e) {}
      }
      // 不再调用 localStorage.clear()，保留记住密码、语言偏好等用户设置
    }
  },
  actions: {
    async login({ commit, dispatch }, { userInfo, rememberMe }) {
      try {
        // ✅ 终极清空
        commit('LOGOUT')

        const loginData = {
          phone: userInfo.phone || userInfo.username || '',
          password: userInfo.password || ''
        }

        console.log('登录请求数据:', loginData)
        console.log('记住我选项:', rememberMe)
        const response = await login(loginData)
        console.log('登录响应:', response)
        
        // 检查响应格式
        if (response.success === true) {
          // 格式1: { success: true, data: { token: '...', userInfo: {...} } }
          const data = response.data
          if (!data) return { success: false, message: '无用户信息' }

          console.log('登录响应数据:', data)
          const token = data.accessToken || data.token
          const userInfoData = data.userInfo || data
          const realUser = {
            id: String(userInfoData.id),
            phone: userInfoData.phone || loginData.phone,
            username: userInfoData.username || loginData.phone,
            ...userInfoData
          }

          // 处理商城Token
          const mallToken = data.mallToken
          if (mallToken) {
            console.log('商城Token:', mallToken)
            commit('SET_MALL_TOKEN', mallToken)
          }

          console.log('真实用户信息:', realUser)
          console.log('Token:', token)

          // ✅ 只存真实用户
          commit('SET_TOKEN', { token, rememberMe })
          commit('SET_USER_INFO', realUser)
          setUserId(realUser.id, rememberMe)
          
          // 保存记住的手机号
          if (rememberMe) {
            setRememberPhone(realUser.phone || loginData.phone)
          }

          // 🎯 登录后：初始化自己的 shareId + 自动绑定待处理的推荐关系
          ShareManager.init(realUser.id).catch(() => {})
          const pendingSid = getPendingShareId()
          if (pendingSid) {
            ShareManager.bindForCurrentUser(pendingSid, realUser.id).then(r => {
              if (r?.ok || r?.skipped) removePendingShareId()
            }).catch(() => {})
          }

          dispatch('message/fetchUnreadCount', null, { root: true })
          return { success: true, message: '登录成功' }
        } else if (response.code === 200 || response.code === 0) {
          // 格式2: { code: 200, data: { token: '...', userInfo: {...} } }
          const data = response.data
          if (!data) return { success: false, message: '无用户信息' }

          console.log('登录响应数据:', data)
          const token = data.accessToken || data.token
          const userInfoData = data.userInfo || data
          const realUser = {
            id: String(userInfoData.id),
            phone: userInfoData.phone || loginData.phone,
            username: userInfoData.username || loginData.phone,
            ...userInfoData
          }

          // 处理商城Token
          const mallToken = data.mallToken
          if (mallToken) {
            console.log('商城Token:', mallToken)
            commit('SET_MALL_TOKEN', mallToken)
          }

          console.log('真实用户信息:', realUser)
          console.log('Token:', token)

          // ✅ 只存真实用户
          commit('SET_TOKEN', { token, rememberMe })
          commit('SET_USER_INFO', realUser)
          setUserId(realUser.id, rememberMe)
          
          // 保存记住的手机号
          if (rememberMe) {
            setRememberPhone(realUser.phone || loginData.phone)
          }

          // 🎯 登录后：初始化自己的 shareId + 自动绑定待处理的推荐关系
          ShareManager.init(realUser.id).catch(() => {})
          const pendingSid = getPendingShareId()
          if (pendingSid) {
            ShareManager.bindForCurrentUser(pendingSid, realUser.id).then(r => {
              if (r?.ok || r?.skipped) removePendingShareId()
            }).catch(() => {})
          }

          dispatch('message/fetchUnreadCount', null, { root: true })
          return { success: true, message: '登录成功' }
        } else {
          return { success: false, message: response.message || response.msg || '登录失败' }
        }

      } catch (err) {
        console.error(err)
        // 🔥 从 AxiosError 中提取后端真实错误消息（密码错误、用户不存在等）
        const backendMsg = err?.response?.data?.message
          || err?.response?.data?.msg
          || err?.message
          || '登录异常'
        return { success: false, message: backendMsg }
      }
    },
    async smsLogin({ commit, dispatch }, { userInfo, rememberMe }) {
      try {
        commit('LOGOUT')
        const response = await apiSmsLogin(userInfo)
        console.log('短信登录响应:', response)
        
        // 检查响应格式 - 兼容多种格式
        if (response.success === true || response.code === 200 || response.code === 0) {
          const data = response.data || response
          if (!data) return { success: false, message: '无用户信息' }

          console.log('短信登录响应数据:', data)
          const token = data.accessToken || data.token
          const userInfoData = data.userInfo || data
          const realUser = {
            id: String(userInfoData.id),
            phone: userInfo.phone || userInfoData.phone,
            username: userInfoData.username || userInfo.phone,
            ...userInfoData
          }

          // 处理商城Token
          const mallToken = data.mallToken
          if (mallToken) {
            console.log('商城Token:', mallToken)
            commit('SET_MALL_TOKEN', mallToken)
            // 保存到cookies中，确保getMallToken()能正确获取
            setMallToken(mallToken)
          }

          console.log('真实用户信息:', realUser)
          console.log('Token:', token)

          // ✅ 只存真实用户
          commit('SET_TOKEN', { token, rememberMe })
          commit('SET_USER_INFO', realUser)
          setUserId(realUser.id, rememberMe)
          
          // 保存记住的手机号
          if (rememberMe) {
            setRememberPhone(realUser.phone || userInfo.phone)
          }

          // 🎯 登录后：初始化自己的 shareId + 自动绑定待处理的推荐关系
          ShareManager.init(realUser.id).catch(() => {})
          const pendingSid = getPendingShareId()
          if (pendingSid) {
            ShareManager.bindForCurrentUser(pendingSid, realUser.id).then(r => {
              if (r?.ok || r?.skipped) removePendingShareId()
            }).catch(() => {})
          }

          dispatch('message/fetchUnreadCount', null, { root: true })
          return { success: true, message: '登录成功' }
        } else {
          return { success: false, message: response.message || response.msg || '登录失败' }
        }
      } catch (e) {
        console.error('短信登录异常:', e)
        const backendMsg = e?.response?.data?.message
          || e?.response?.data?.msg
          || e?.message
          || '短信登录异常'
        return { success: false, message: backendMsg }
      }
    },
    async sendSmsCode({ commit }, request) {
      try {
        const res = await apiSendSmsCode(request)
        return res.code === 200 ? { success: true } : { success: false }
      } catch (e) {
        return { success: false }
      }
    },
    logout({ commit }) {
      commit('LOGOUT')
    },
    updateUserInfo({ commit }, info) {
      commit('SET_USER_INFO', info)
    },
    async submitUpdateUserInfo({ commit, state }, userInfo) {
      try {
        console.log('🔍 [STORE DEBUG] submitUpdateUserInfo 收到的 userInfo.username =', JSON.stringify(userInfo?.username))
        console.log('🔍 [STORE DEBUG] submitUpdateUserInfo 收到的 userInfo keys =', Object.keys(userInfo || {}))
        console.log('submitUpdateUserInfo: 保存前的userInfo:', userInfo)
        console.log('submitUpdateUserInfo: 保存前的products:', userInfo.products)
        console.log('submitUpdateUserInfo: 保存前的products类型:', typeof userInfo.products)
        console.log('submitUpdateUserInfo: 保存前的photos:', userInfo.photos)
        console.log('submitUpdateUserInfo: 保存前的photos类型:', typeof userInfo.photos)
        console.log('submitUpdateUserInfo: 保存前的companies(JSON) =', JSON.stringify(userInfo.companies),
          '| isArray =', Array.isArray(userInfo.companies),
          '| length =', Array.isArray(userInfo.companies) ? userInfo.companies.length : 'N/A')
        
        // 数组字段的JSON转换由api/user.js处理，这里不需要重复转换
        // ✅ 再过滤一次只读/敏感唯一字段（email/密码哈希/openid等），防唯一键冲突
        const BLACKLIST_KEYS = new Set([
          'email', 'passwordHash', 'password_hash', 'password',
          'openid', 'unionid', 'createdAt', 'created_at',
          'isAdmin', 'is_admin', 'userId'
        ])
        const dataToSave = {}
        Object.keys(userInfo || {}).forEach(key => {
          if (BLACKLIST_KEYS.has(key)) return
          dataToSave[key] = userInfo[key]
        })
        
        const { updateUser } = await import('@/api/user')
        const res = await updateUser(dataToSave)
        
        console.log('submitUpdateUserInfo: 保存后的响应:', res)
        console.log('submitUpdateUserInfo: 保存后的data:', res.data)
        console.log('submitUpdateUserInfo: 保存后的products:', res.data.products)
        console.log('submitUpdateUserInfo: 保存后的products类型:', typeof res.data.products)
        
        if (res.code === 200 && res.data) {
          // ✅ 合并而非替换：保留 store 中已有的字段（如 avatar），防止后端返回不完整数据
          const mergedData = { ...state.userInfo, ...res.data }
          // ✅ 解析后端返回的JSON字符串字段（products/photos/companies/images/cases/videos）
          const jsonFields = ['images', 'products', 'cases', 'videos', 'photos', 'companies']
          jsonFields.forEach(field => {
            if (mergedData[field]) {
              if (typeof mergedData[field] === 'string' && mergedData[field].trim() !== '') {
                try { mergedData[field] = JSON.parse(mergedData[field]) } catch (e) { mergedData[field] = [] }
              } else if (!Array.isArray(mergedData[field])) {
                mergedData[field] = []
              }
            } else {
              mergedData[field] = []
            }
          })
          // showSections 解析 - 确保所有6个键都存在
          const DEFAULT_SECTIONS = { company: true, video: true, resume: true, cases: false, products: false, photos: true }
          const ALL_KEYS = Object.keys(DEFAULT_SECTIONS)
          let parsedSections = null
          // 检查后端是否返回了有效的 showSections
          const backendHasShowSections = res.data && res.data.showSections !== null && res.data.showSections !== undefined && res.data.showSections !== ''
          if (backendHasShowSections) {
            if (typeof res.data.showSections === 'string' && res.data.showSections.trim() !== '') {
              try { parsedSections = JSON.parse(res.data.showSections) } catch (e) { parsedSections = null }
            } else if (typeof res.data.showSections === 'object') {
              parsedSections = res.data.showSections
            }
          }
          // 构建完整对象，确保所有键都存在
          let resolvedSections
          if (parsedSections && typeof parsedSections === 'object') {
            resolvedSections = { ...DEFAULT_SECTIONS }
            ALL_KEYS.forEach(k => {
              if (parsedSections[k] !== undefined) {
                resolvedSections[k] = !!parsedSections[k]
              }
            })
          } else if (backendHasShowSections) {
            // 后端返回了值但解析失败，使用默认
            resolvedSections = { ...DEFAULT_SECTIONS }
          } else {
            // 后端没有返回 showSections，保留 store 中已有的值
            resolvedSections = state.userInfo?.showSections || { ...DEFAULT_SECTIONS }
          }
          console.log('[submitUpdateUserInfo] showSections 处理: backendHasShowSections =', backendHasShowSections, 'resolved =', JSON.stringify(resolvedSections))
          mergedData.showSections = resolvedSections
          if (mergedData.moduleOrder && typeof mergedData.moduleOrder === 'string' && mergedData.moduleOrder.trim() !== '') {
            try { mergedData.moduleOrder = JSON.parse(mergedData.moduleOrder) } catch (e) {}
          }
          commit('SET_USER_INFO', mergedData)
          return { success: true }
        }
        return { success: false }
      } catch (e) {
        console.error('submitUpdateUserInfo: 保存失败:', e)
        return { success: false }
      }
    },
    async fetchMallUserInfo({ commit, state }) {
      try {
        const res = await getMallUserInfo(state.mallToken)
        if (res.code === 200) commit('SET_MALL_INFO', res.data)
        return { success: true }
      } catch (e) {
        return { success: false }
      }
    },
    async wechatLogin({ commit, dispatch }, loginData) {
      try {
        commit('LOGOUT') // 先清空之前的登录信息

        const { token, userInfo } = loginData
        if (!token) {
          return { success: false, message: '无效的token' }
        }

        // 保存用户信息
        const realUser = {
          id: String(userInfo.id || userInfo.userId),
          phone: userInfo.phone || '',
          username: userInfo.username || userInfo.nickname || '',
          nickname: userInfo.nickname || '',
          avatar: userInfo.avatar || '',
          openid: userInfo.openid || '',
          ...userInfo
        }

        commit('SET_TOKEN', token)
        commit('SET_USER_INFO', realUser)
        setUserId(realUser.id)

        // 🎯 登录后：初始化自己的 shareId + 自动绑定待处理的推荐关系
        ShareManager.init(realUser.id).catch(() => {})
        const pendingSid = getPendingShareId()
        if (pendingSid) {
          ShareManager.bindForCurrentUser(pendingSid, realUser.id).then(r => {
            if (r?.ok || r?.skipped) removePendingShareId()
          }).catch(() => {})
        }

        dispatch('message/fetchUnreadCount', null, { root: true })
        return { success: true, message: '微信登录成功' }
      } catch (err) {
        console.error('微信登录异常:', err)
        const backendMsg = err?.response?.data?.message
          || err?.response?.data?.msg
          || err?.message
          || '微信登录异常'
        return { success: false, message: backendMsg }
      }
    },
  }
}

const messageModule = {
  namespaced: true,
  state: { unreadCount: 0 },
  getters: { unreadCount: s => s.unreadCount },
  mutations: { SET_UNREAD_COUNT(s, c) { s.unreadCount = c } },
  actions: {
    async fetchUnreadCount({ commit, rootState }) {
      try {
        const userId = rootState.user.userInfo?.id
        if (!userId) {
          commit('SET_UNREAD_COUNT', 0)
          return
        }
        const { getUnreadCount } = await import('@/api/friend')
        const response = await getUnreadCount({ userId })
        console.log('获取未读消息数量响应:', response)
        
        if (response.code === 0 || response.code === 200 || response.success) {
          const data = response.data
          let total = 0
          
          // 处理多种可能的数据格式
          if (typeof data === 'number') {
            // 格式1: 直接返回数字
            total = data
          } else if (data.total !== undefined) {
            // 格式2: { total: 5 }
            total = data.total
          } else if (data.friend_request !== undefined) {
            // 格式3: { friend_request: 3 }
            total = data.friend_request
          } else if (Array.isArray(data)) {
            // 格式4: 数组格式，计算所有未读消息总数
            total = data.reduce((sum, item) => sum + (item.unreadCount || item.unread || 0), 0)
          } else if (typeof data === 'object') {
            // 格式5: 对象格式，尝试求和所有数字类型的值
            total = Object.values(data).reduce((sum, val) => {
              return sum + (typeof val === 'number' ? val : 0)
            }, 0)
          }
          
          console.log('计算的未读消息总数:', total)
          commit('SET_UNREAD_COUNT', total)
        } else {
          commit('SET_UNREAD_COUNT', 0)
        }
      } catch (error) {
        console.error('获取未读消息数量失败:', error)
        // 不清空现有数据，保持之前的值
      }
    }
  }
}

const cardModule = {
  namespaced: true,
  state: { cardList: [], currentCard: null, loading: false },
  getters: { cardList: s => s.cardList, currentCard: s => s.currentCard },
  mutations: {
    SET_CARD_LIST(s, l) { s.cardList = l },
    SET_CURRENT_CARD(s, c) { s.currentCard = c },
  },
  actions: {}
}

const friendModule = {
  namespaced: true,
  state: { friendList: [], friendCount: 0, loading: false },
  getters: { friendList: s => s.friendList },
  mutations: {
    SET_FRIEND_LIST(s, l) { s.friendList = l; s.friendCount = l.length },
    SET_LOADING(s, loading) { s.loading = loading }
  },
  actions: {
    async fetchFriendList({ commit, rootState }) {
      try {
        commit('SET_LOADING', true)
        const userInfo = rootState.user.userInfo
        const userId = userInfo?.id || userInfo?.userId || ''
        
        console.log('获取好友列表，用户ID:', userId)
        
        if (!userId) {
          console.warn('没有用户ID，无法获取好友列表')
          return
        }
        
        const res = await apiGetFriendList({ userId })
        console.log('获取好友列表响应:', res)
        
        if (res.code === 200 || res.code === 0) {
          commit('SET_FRIEND_LIST', res.data || [])
        }
      } catch (error) {
        console.error('获取好友列表失败:', error)
      } finally {
        commit('SET_LOADING', false)
      }
    },
    async deleteFriend({ commit, dispatch, rootState }, friendId) {
      try {
        const userInfo = rootState.user.userInfo
        const userId = userInfo?.id || userInfo?.userId || ''
        
        const res = await deleteFriend(friendId, userId)
        if (res.code === 200 || res.code === 0) {
          dispatch('fetchFriendList')
        }
      } catch (error) {
        console.error('删除好友失败:', error)
      }
    }
  }
}

export default createStore({
  state: {
    appName: '数字名片',
    theme: 'light',
    isMobile: window.innerWidth < 768
  },
  mutations: {
    SET_THEME(s, t) { s.theme = t },
    SET_IS_MOBILE(s, m) { s.isMobile = m }
  },
  actions: {
    // 🟢【关键修复】从本地存储加载token和用户信息，保持登录状态
    async initApp({ commit, dispatch }) {
      try {
        const token = getToken()
        const userInfo = getUserInfo()
        const mallToken = getMallToken()

        console.log('initApp - 当前token:', token)
        console.log('initApp - 当前用户:', userInfo)
        console.log('initApp - 当前商城Token:', mallToken)

        // ✅ 强制过滤 test_token
        if (!token || token === 'test_token' || !userInfo || !userInfo.id) {
          console.warn('initApp - 无效token，不自动登录')
          return
        }

        // ✅ 只保存真实有效用户
        commit('user/SET_TOKEN', token)
        commit('user/SET_USER_INFO', userInfo)
        // 设置用户ID
        setUserId(userInfo.id)
        
        // 加载商城Token
        if (mallToken) {
          commit('user/SET_MALL_TOKEN', mallToken)
        }

        // 🟢【新增】登录成功后立即获取未读消息数
        dispatch('message/fetchUnreadCount')

        // 🎯 自动恢复登录后：初始化自己的 shareId + 自动绑定待处理的推荐关系
        ShareManager.init(userInfo.id).catch(() => {})
        const pendingSid = getPendingShareId()
        if (pendingSid) {
          ShareManager.bindForCurrentUser(pendingSid, userInfo.id).then(r => {
            if (r?.ok || r?.skipped) removePendingShareId()
          }).catch(() => {})
        }

      } catch (e) {
        console.error('initApp error', e)
      }
    }
  },
  modules: {
    user: userModule,
    card: cardModule,
    friend: friendModule,
    message: messageModule
  }
})